import styles from './index.module.css'

import React from 'react'

import { SEARCH_APPEARS_ON_FILTER_FIELD } from '@disco/constants/search'

import { FilterComponentProps, FilterOperator } from 'types/filter-page'

import { Flex, Text, TextField } from 'components/ds'

import { useKeyPress } from 'hooks/use-key-press'

import { FilterOperatorSelector } from '../../filter-operator-selector'
import {
  getBetweenRangeErrors,
  NO_PLAYLISTS_BADGE_LABEL,
  NO_PLAYLISTS_FILTER_VALUE,
} from '../../utils'
import { FilterErrorText } from '../components'
import { useNumericValidation } from './use-numeric-validation'

type NumberFormProps = FilterComponentProps & {
  yearOperators?: boolean
}

// UI-only radio key for the "No playlists" option. Deliberately not part of
// FilterOperator — the backend never receives it; the option commits as
// `equals 0`.
const NO_PLAYLISTS_KEY = 'no_playlists'
type NumberOperatorKey = FilterOperator | typeof NO_PLAYLISTS_KEY

type NumberOperatorOption = {
  label?: string
  value: NumberOperatorKey
  component: React.ReactNode
}

// Valueless shortcut, shown only for the "Appears on" field; commits as equals 0.
const NO_PLAYLISTS_OPTION: NumberOperatorOption = {
  value: NO_PLAYLISTS_KEY,
  label: NO_PLAYLISTS_BADGE_LABEL,
  component: null,
}

const NumberForm = ({
  label,
  field,
  value,
  onChange,
  operator = 'equals',
  yearOperators = false,
  onPress,
  onValidityChange,
}: NumberFormProps) => {
  const { handleKeyDown } = useKeyPress('Enter', onPress)

  // The "Appears on" (playlists count) field offers a "No playlists" shortcut.
  const showNoPlaylistsOption = field === SEARCH_APPEARS_ON_FILTER_FIELD

  // "No playlists" and a typed `equals 0` are the same query, so derive the
  // radio selection from props rather than local state. Keeping it controlled
  // means it can't desync from the operator prop when that's reset out-of-band
  // (e.g. when FilterFooter's Clear leaves the popover open).
  const selectedKey: NumberOperatorKey =
    showNoPlaylistsOption &&
    operator === 'equals' &&
    value === NO_PLAYLISTS_FILTER_VALUE
      ? NO_PLAYLISTS_KEY
      : operator

  const { letterErrors, hasActiveLetterError, handleDigitChange } =
    useNumericValidation({ operator, onValidityChange })

  const { startMissing, endMissing, isIncomplete } =
    getBetweenRangeErrors(value)

  // Text input with numeric inputMode so we can intercept and reject letters.
  const createNumberInput = (
    inputValue: string,
    onChangeHandler: (_value: string) => void,
    shouldAutoFocus = true,
    hasError = false,
    doubleInput = false,
  ) => (
    <TextField.Root
      type="text"
      inputMode="numeric"
      value={inputValue}
      onChange={e => onChangeHandler(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={label}
      autoFocus={shouldAutoFocus}
      color={hasError ? 'red' : undefined}
      className={[
        doubleInput ? styles.betweenInput : styles.single,
        hasError ? styles.error : '',
      ]
        .filter(Boolean)
        .join(' ')}
    />
  )

  const singleInput = (
    <Flex direction="column" gap="1">
      {createNumberInput(
        value || '',
        raw =>
          handleDigitChange('single', raw, digits =>
            onChange(digits, operator),
          ),
        true,
        letterErrors.single,
      )}
      {letterErrors.single && (
        <FilterErrorText>Only numbers allowed</FilterErrorText>
      )}
    </Flex>
  )

  const allFilterOperators: NumberOperatorOption[] = [
    {
      value: 'equals',
      component: singleInput,
    },
    {
      value: 'between',
      component: (
        <Flex direction="column" gap="1">
          <Flex gap="2" align="center" className={styles.betweenContainer}>
            {createNumberInput(
              value?.[0] || '',
              raw =>
                handleDigitChange('start', raw, digits =>
                  onChange([digits, value?.[1] ?? ''], operator),
                ),
              true,
              letterErrors.start || startMissing,
              true,
            )}
            <Text size="2">-</Text>
            {createNumberInput(
              value?.[1] || '',
              raw =>
                handleDigitChange('end', raw, digits =>
                  onChange([value?.[0] ?? '', digits], operator),
                ),
              false,
              letterErrors.end || endMissing,
              true,
            )}
          </Flex>
          {hasActiveLetterError ? (
            <FilterErrorText>Only numbers allowed</FilterErrorText>
          ) : (
            isIncomplete && <FilterErrorText>Enter both values</FilterErrorText>
          )}
        </Flex>
      ),
    },
    {
      value: yearOperators ? 'lte' : 'lt',
      component: singleInput,
    },
    {
      value: yearOperators ? 'gte' : 'gt',
      component: singleInput,
    },
    ...(showNoPlaylistsOption ? [NO_PLAYLISTS_OPTION] : []),
  ]

  // "No playlists" has no input, so commit its `equals 0` value on selection.
  const handleOperatorChange = (
    newValue: string | [string, string] | undefined,
    newKey: NumberOperatorKey,
  ) => {
    if (newKey === NO_PLAYLISTS_KEY) {
      onChange(NO_PLAYLISTS_FILTER_VALUE, 'equals')
      return
    }
    onChange(newValue, newKey)
  }

  return (
    <FilterOperatorSelector<NumberOperatorKey>
      operator={selectedKey}
      onChange={handleOperatorChange}
      operators={allFilterOperators}
    />
  )
}

export default NumberForm
